import type { RecoveryActionName } from "../recovery/index.js";
import type { ItemDocument, VaultItem } from "./index.js";

/**
 * Item detail (W-6), headless: the one action an item's status offers,
 * whether Edit/Delete and document changes are allowed, the
 * `POST /items/validate` code → RecoveryView mapping, and the `PUT /items`
 * payload every web change starts from (the stored item).
 */

const COLLECTOR = new Set(["art", "wine", "whisky", "tradingCard"]);
const LEGACY = new Set(["watch", "camera", "laptop", "jewellery", "handbag"]);

export function isCollectorItem(item: Pick<VaultItem, "classification">): boolean {
  return COLLECTOR.has(item.classification ?? "");
}

export function isLegacyItem(item: Pick<VaultItem, "classification">): boolean {
  return LEGACY.has(item.classification ?? "");
}

// ---------------------------------------------------------------- action

export type DetailAction =
  /** uninsured, expired, approved_underwriter_review. */
  | { kind: "choose_plan" }
  /** insured, claiming, discontinued: View policy, plus Extend coverage when extensible. */
  | { kind: "view_policy"; policyId: string; extend: boolean }
  /** pending_*: a Pending review banner, no action. */
  | { kind: "pending" }
  /** rejected_*: a Failed badge, the reason banner and Contact support. */
  | { kind: "rejected" }
  | { kind: "none" };

const CHOOSE_PLAN = new Set(["uninsured", "expired", "approved_underwriter_review"]);
const VIEW_POLICY = new Set(["insured", "claiming", "discontinued"]);

/** The action set for an item's status. `extensible` comes from `GET /policy/:policyId` (SG). */
export function detailAction(
  item: Pick<VaultItem, "status" | "policyId">,
  opts: { extensible?: boolean | null } = {},
): DetailAction {
  const s = item.status ?? "";
  if (CHOOSE_PLAN.has(s)) return { kind: "choose_plan" };
  if (VIEW_POLICY.has(s)) {
    // Without a policy number there is nothing to link to.
    if (!item.policyId) return { kind: "none" };
    return { kind: "view_policy", policyId: item.policyId, extend: opts.extensible === true };
  }
  if (s.startsWith("pending_")) return { kind: "pending" };
  if (s.startsWith("rejected_")) return { kind: "rejected" };
  return { kind: "none" };
}

/**
 * `GET /policy/:policyId` is fetched only in SG, only for an insured item
 * with a policy number (HK's `/policy` has no `extensible`).
 */
export function shouldFetchPolicy(item: Pick<VaultItem, "status" | "policyId">, region: string): boolean {
  return region === "sg" && item.status === "insured" && !!item.policyId;
}

/**
 * Edit and Delete exist only while the item has never had a policy:
 * `policyId` is never cleared on lapse. (A future-dated purchase has a
 * policy before `policyId` is set; the server's 409 covers that window.)
 */
export function canEditOrDelete(item: Pick<VaultItem, "policyId">): boolean {
  return !item.policyId;
}

/** Receipts and selfies can change while uninsured, or when the policy is extensible (as the app). */
export function canManageDocuments(item: Pick<VaultItem, "status">, extensible: boolean | null | undefined): boolean {
  return item.status === "uninsured" || extensible === true;
}

// ---------------------------------------------------------------- validate

export type ValidateOutcome =
  /** `all_good`: on to Choose plan. */
  | { kind: "proceed" }
  /** HK `over_underwriting_limit`: the item was sent for review; the server message as an info banner. */
  | { kind: "info" }
  /**
   * A RecoveryView with the server message. `target` says where
   * `upload_documents` leads: the Documents heading, or the edit route's
   * photos step.
   */
  | { kind: "recovery"; action: RecoveryActionName; target?: "documents" | "photos" };

/**
 * `POST /items/validate` code → what the detail screen shows. Never throws:
 * an unknown code is `contact_support`.
 */
export function validateCodeToAction(
  code: string,
  opts: {
    canEdit: boolean;
    collector?: boolean;
    region?: string;
    /** Receipts and selfies can be added here (`canManageDocuments`); defaults to true. */
    canManageDocuments?: boolean;
  },
): ValidateOutcome {
  const support: ValidateOutcome = { kind: "recovery", action: "contact_support" };
  const editOrSupport: ValidateOutcome = opts.canEdit ? { kind: "recovery", action: "update_item_details" } : support;
  // Collector documents are read-only on web; so are legacy ones outside
  // uninsured/extensible. Pointing at a section with no upload would be a dead end.
  const manageable = !opts.collector && opts.canManageDocuments !== false;
  switch (code) {
    case "all_good":
      return { kind: "proceed" };
    case "missing_item_images":
      if (opts.collector) return support;
      if (opts.canEdit) return { kind: "recovery", action: "upload_documents", target: "photos" };
      return manageable ? { kind: "recovery", action: "upload_documents", target: "documents" } : support;
    case "missing_documents":
    case "missing_selfies":
      return manageable ? { kind: "recovery", action: "upload_documents", target: "documents" } : support;
    case "user_not_authenticated":
      return { kind: "recovery", action: "verify_identity" };
    case "item_too_old":
      return editOrSupport;
    case "over_underwriting_limit":
      return opts.region === "hk" ? { kind: "info" } : editOrSupport;
    default:
      return support;
  }
}

// ---------------------------------------------------------------- documents

/** The document's Storage location when it is under the caller's `users/{uid}/` prefix (never `images/`). */
export function ownStorageLocation(doc: Pick<ItemDocument, "location">, uid: string | null | undefined): string | undefined {
  if (!uid || typeof doc.location !== "string") return undefined;
  return doc.location.startsWith(`users/${uid}/`) ? doc.location : undefined;
}

/** Documents of one classification, ordered by `order_index`. */
export function documentsOf(item: Pick<VaultItem, "documents">, classification: string): ItemDocument[] {
  return (item.documents ?? [])
    .filter((d) => d.classification === classification && !!d.url)
    .sort((a, b) => (a.order_index ?? Infinity) - (b.order_index ?? Infinity));
}

/**
 * The stored documents plus `added`, each added document numbered after the
 * stored ones of its classification. `removed` URLs are dropped.
 */
export function mergeDocuments(
  stored: readonly ItemDocument[],
  added: readonly ItemDocument[],
  removed: ReadonlySet<string> = new Set(),
): ItemDocument[] {
  const kept = stored.filter((d) => !removed.has(d.url));
  const next = new Map<string, number>();
  for (const d of kept) {
    const n = typeof d.order_index === "number" ? d.order_index + 1 : 0;
    next.set(d.classification, Math.max(next.get(d.classification) ?? 0, n));
  }
  const out = [...kept];
  for (const d of added) {
    const i = next.get(d.classification) ?? 0;
    next.set(d.classification, i + 1);
    out.push({ ...d, order_index: i });
  }
  return out;
}

// ---------------------------------------------------------------- PUT payload

export interface ItemUpdatePayload {
  id: string;
  classification?: string;
  classId?: number;
  valueAmount?: number;
  valueCurrency: string;
  purchasedAt?: string;
  brand?: string;
  model?: string;
  type?: string;
  name?: string;
  condition?: string;
  listAccessory?: string[];
  isBrand?: boolean;
  universalId?: string;
  notes?: string;
  serialNumber?: string;
  attributes?: Record<string, unknown>;
  documents: ItemDocument[];
}

/** Keys a PUT carries unchanged from the stored item (nulls are left out: the DTO rejects them). */
const CARRIED = [
  "classification",
  "classId",
  "valueAmount",
  "purchasedAt",
  "brand",
  "model",
  "type",
  "name",
  "condition",
  "listAccessory",
  "isBrand",
  "universalId",
  "notes",
  "serialNumber",
] as const;

/**
 * The `PUT /items` body for a document change: the stored item's fields
 * (so the server values the item as before, e.g. a picked watch's
 * `universalId`), `valueCurrency` (stored, else the tenant's) and the new
 * document list. Collector attributes are not sent: they stay as stored.
 */
export function withDocuments(item: VaultItem, documents: readonly ItemDocument[], tenantCurrency: string): ItemUpdatePayload {
  const out: Record<string, unknown> = { id: item._id };
  for (const k of CARRIED) {
    const v = item[k];
    if (v !== undefined && v !== null) out[k] = Array.isArray(v) ? [...v] : v;
  }
  out.valueCurrency = item.valueCurrency || tenantCurrency;
  out.documents = documents.map((d) => ({ ...d }));
  return out as unknown as ItemUpdatePayload;
}
